import type {
  GuildWarRosterBoard,
  GuildWarRosterTeamKey,
  GuildWarTeamRoster,
} from "./types.js"

import {
  getGuildWarRosterTeamKeys,
  getGuildWarTeamCapacity,
  getGuildWarTeamMemberSlots,
  getGuildWarTeamRosterMembers,
  hasGuildWarViceCommanderSlot,
} from "./roster-board.js"
import { GUILD_WAR_TEAM_DISPLAY } from "./types.js"
export { getGuildWarTeamRosterMembers } from "./roster-board.js"

export type GuildWarRosterHealthStatus = "complete" | "warning" | "incomplete"

export type GuildWarRosterTeamValidationSummary = {
  team: GuildWarRosterTeamKey
  capacity: number
  assignedCount: number
  openSlots: number
  missingCommander: boolean
  missingViceCommander: boolean
  missingMemberSlots: number
  status: GuildWarRosterHealthStatus
  issues: string[]
}

export type GuildWarRosterBoardValidationSummary = {
  teamSummaries: GuildWarRosterTeamValidationSummary[]
  totalAssigned: number
  uniqueAssigned: number
  duplicateMemberIds: string[]
  halftimeFilled: number
  halftimeMissing: number
  hasIssues: boolean
  issues: string[]
}

export const getGuildWarAssignedMemberIds = (
  board: GuildWarRosterBoard,
): string[] =>
  getGuildWarRosterTeamKeys(board).flatMap((team) =>
    getGuildWarTeamRosterMembers(board[team]!),
  )

export const getGuildWarTeamAssignedCount = (
  team: GuildWarTeamRoster,
): number => getGuildWarTeamRosterMembers(team).length

export const getGuildWarTeamOpenSlotCount = (
  team: GuildWarTeamRoster,
): number => getGuildWarTeamCapacity(team) - getGuildWarTeamAssignedCount(team)

export const getGuildWarTeamValidationSummary = (
  teamKey: GuildWarRosterTeamKey,
  team: GuildWarTeamRoster,
): GuildWarRosterTeamValidationSummary => {
  const capacity = getGuildWarTeamCapacity(team)
  const assignedCount = getGuildWarTeamAssignedCount(team)
  const openSlots = capacity - assignedCount
  const missingCommander = team.commander === null
  const missingViceCommander =
    hasGuildWarViceCommanderSlot(team) && team.viceCommander === null
  const missingMemberSlots = getGuildWarTeamMemberSlots(team).filter(
    (value) => value === null,
  ).length

  const issues: string[] = []
  if (missingCommander) {
    issues.push("Missing commander")
  }
  if (missingViceCommander) {
    issues.push("Missing vice commander")
  }
  if (missingMemberSlots > 0) {
    issues.push(
      `${missingMemberSlots} open member slot${missingMemberSlots === 1 ? "" : "s"}`,
    )
  }

  return {
    team: teamKey,
    capacity,
    assignedCount,
    openSlots,
    missingCommander,
    missingViceCommander,
    missingMemberSlots,
    status:
      assignedCount === capacity
        ? "complete"
        : assignedCount >= capacity - 2
          ? "warning"
          : "incomplete",
    issues,
  }
}

export const getGuildWarRosterBoardValidationSummary = (
  board: GuildWarRosterBoard,
): GuildWarRosterBoardValidationSummary => {
  const assignedMemberIds = getGuildWarAssignedMemberIds(board)
  const uniqueAssignedIds = new Set<string>()
  const duplicateIds = new Set<string>()

  for (const memberId of assignedMemberIds) {
    if (uniqueAssignedIds.has(memberId)) {
      duplicateIds.add(memberId)
      continue
    }

    uniqueAssignedIds.add(memberId)
  }

  const teamSummaries = getGuildWarRosterTeamKeys(board).map((team) =>
    getGuildWarTeamValidationSummary(team, board[team]!),
  )

  const halftimeFilled = board.halftimePerformers.filter(Boolean).length
  const issues: string[] = []

  if (duplicateIds.size > 0) {
    issues.push(
      `Duplicate roster assignments: ${[...duplicateIds].sort((a, b) => a.localeCompare(b)).join(", ")}`,
    )
  }

  const incompleteTeams = teamSummaries.filter(
    (summary) => summary.status !== "complete",
  )
  if (incompleteTeams.length > 0) {
    issues.push(
      `Incomplete teams: ${incompleteTeams.map((summary) => GUILD_WAR_TEAM_DISPLAY[summary.team].label).join(", ")}`,
    )
  }

  if (halftimeFilled < 3) {
    issues.push(
      `${3 - halftimeFilled} halftime slot${halftimeFilled === 2 ? "" : "s"} open`,
    )
  }

  return {
    teamSummaries,
    totalAssigned: assignedMemberIds.length,
    uniqueAssigned: uniqueAssignedIds.size,
    duplicateMemberIds: [...duplicateIds].sort((a, b) => a.localeCompare(b)),
    halftimeFilled,
    halftimeMissing: 3 - halftimeFilled,
    hasIssues: issues.length > 0,
    issues,
  }
}
